import { z } from 'zod';

import { LIMITS } from '../constants.js';

import { isoDateTimeSchema, timeOfDaySchema, uuidSchema } from './common.js';

export const cadenceSchema = z.enum(['daily', 'weekly', 'biweekly', 'monthly']);
export type Cadence = z.infer<typeof cadenceSchema>;

/** Biweekly ISO-week parity — 'even' fires on even weeks, 'odd' on odd weeks. */
export const biweeklyParitySchema = z.enum(['even', 'odd']);
export type BiweeklyParity = z.infer<typeof biweeklyParitySchema>;

/** 0 = Monday … 6 = Sunday */
export const weekdaySchema = z.coerce.number().int().min(0).max(6);

// ---------- Rule ----------

export const checkInRuleSchema = z.object({
  id: uuidSchema,
  clientId: uuidSchema,
  question: z.string().min(1).max(LIMITS.checkInQuestionMax),
  cadence: cadenceSchema,
  biweeklyParity: biweeklyParitySchema,
  weekdays: z.array(weekdaySchema),
  timeOfDay: timeOfDaySchema,
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  lastRunAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type CheckInRuleDto = z.infer<typeof checkInRuleSchema>;

/** Extended list shape — includes participants and comment count. */
export const checkInRuleSummarySchema = checkInRuleSchema.extend({
  assigneeIds: z.array(uuidSchema),
  commentCount: z.number().int().min(0),
});
export type CheckInRuleSummaryDto = z.infer<typeof checkInRuleSummarySchema>;

export const createCheckInRuleSchema = z.object({
  question: z.string().min(1, 'Введите вопрос').max(LIMITS.checkInQuestionMax),
  cadence: cadenceSchema.default('daily'),
  biweeklyParity: biweeklyParitySchema.default('even'),
  weekdays: z.array(weekdaySchema).default([]),
  timeOfDay: timeOfDaySchema.default('09:00'),
  isActive: z.boolean().default(true),
  assigneeIds: z.array(uuidSchema).default([]),
});
export type CreateCheckInRuleDto = z.infer<typeof createCheckInRuleSchema>;

export const updateCheckInRuleSchema = z
  .object({
    question: z.string().min(1).max(LIMITS.checkInQuestionMax),
    cadence: cadenceSchema,
    biweeklyParity: biweeklyParitySchema,
    weekdays: z.array(weekdaySchema),
    timeOfDay: timeOfDaySchema,
    isActive: z.boolean(),
    sortOrder: z.number().int(),
    assigneeIds: z.array(uuidSchema),
  })
  .partial();
export type UpdateCheckInRuleDto = z.infer<typeof updateCheckInRuleSchema>;

// ---------- Comments ----------

export const checkInCommentSchema = z.object({
  id: uuidSchema,
  ruleId: uuidSchema,
  authorId: uuidSchema.nullable(),
  text: z.string().min(1),
  kind: z.enum(['user', 'status']),
  createdAt: isoDateTimeSchema,
});
export type CheckInCommentDto = z.infer<typeof checkInCommentSchema>;

export const createCheckInCommentSchema = z.object({
  text: z.string().min(1, 'Введите текст').max(LIMITS.commentTextMax),
});
export type CreateCheckInCommentDto = z.infer<typeof createCheckInCommentSchema>;
